/* ============================================================================
 * i18n/index.js — react-i18next bootstrap
 *
 * Loads English (default) + Spanish translation namespaces, persists the
 * user's choice in localStorage (key: kocore.lang), and exposes a tiny
 * helper API (`setLanguage`, `getLanguage`).
 *
 * To add a new locale:
 *   1. Drop a file at `i18n/locales/<lang>.json`
 *   2. Add it to the `resources` map below.
 *   3. Add the option to the language picker in Settings.
 * ========================================================================== */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import es from './locales/es.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English',  shortLabel: 'EN' },
  { code: 'es', label: 'Español',  shortLabel: 'ES' },
];

export const DEFAULT_LANGUAGE = 'en';
export const STORAGE_KEY = 'kocore.lang';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true, // map 'en-US' → 'en'
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
    react: {
      useSuspense: false, // we ship resources synchronously, no async load
    },
  });

export const setLanguage = (code) => {
  if (!SUPPORTED_LANGUAGES.find((l) => l.code === code)) return;
  i18n.changeLanguage(code);
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch (_) {
    /* private mode / storage disabled — silent */
  }
};

export const getLanguage = () => {
  const cur = i18n.resolvedLanguage || i18n.language || DEFAULT_LANGUAGE;
  return SUPPORTED_LANGUAGES.find((l) => l.code === cur) ? cur : DEFAULT_LANGUAGE;
};

export default i18n;
